import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowLeft, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Do I need to install any software?",
      answer: "No — MoveMate is 100% cloud-based. Your team can use it on mobile, tablet, or desktop instantly. Just log in through your web browser or download our mobile app for iOS and Android."
    },
    {
      question: "Can I use MoveMate for storage-only operations?",
      answer: "Yes! Many companies use MoveMate exclusively for warehouse and inventory tracking. Our storage management features include container tracking, QR code labeling, and full chain of custody visibility."
    },
    {
      question: "Does the mobile app work offline?",
      answer: "Yes — the app stores data offline and syncs automatically when connected. Your team can continue scanning items, taking photos, and updating job statuses even without an internet connection."
    },
    {
      question: "Can I import my existing customer records?",
      answer: "Absolutely. Our onboarding team helps you import jobs, storage items, and customers from your existing systems. We support CSV imports and can assist with custom data migration."
    },
    {
      question: "Is there a long-term contract?",
      answer: "No — our subscriptions renew month-to-month with no long-term commitments required. You can upgrade, downgrade, or cancel your subscription at any time."
    },
    {
      question: "What kind of support do you offer?",
      answer: "We provide email and chat support for all plans, onboarding sessions to get your team started, and dedicated account managers for Enterprise customers. Our support team is available during business hours."
    },
    {
      question: "How does the QR code inventory system work?",
      answer: "Our system generates unique QR codes for each item. Simply print the labels, attach them to items, and scan with our mobile app. Each scan shows the item's photos, description, room assignment, and current container location."
    },
    {
      question: "Can customers track their items in real-time?",
      answer: "Yes! Customers get access to their own portal where they can view job status, track their items, see driver location during delivery, receive notifications, and communicate directly with your team."
    },
    {
      question: "How secure is my data?",
      answer: "We take security seriously. All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. We perform daily encrypted backups stored across multiple secure data centers. Role-based permissions ensure your team only sees what they need."
    },
    {
      question: "Can I customize MoveMate with my company branding?",
      answer: "Yes! Our White Label plan includes full custom branding with your logo, colors, and custom email templates. You can even use your own domain for a fully branded experience."
    },
    {
      question: "Do you integrate with other tools?",
      answer: "Yes, MoveMate integrates with Stripe for payments, Google Calendar for scheduling, and supports SMS and email notifications. We're constantly adding new integrations based on customer feedback."
    },
    {
      question: "How do I get started?",
      answer: "Simply sign up for a free 14-day trial — no credit card required. You'll have access to all features during the trial. Our onboarding team will help you set up your account and import your existing data."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="FAQ - Frequently Asked Questions"
        description="Find answers to common questions about MoveMate. Learn about pricing, features, mobile app, data security, and how to get started with our removal company management software."
        url="https://admin.movematebend.co.uk/faq"
      />
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold">
            <img src="/logo.png" alt="MoveMate" className="h-8" /> MoveMate
          </Link>
          <div className="flex gap-4">
            <Link to="/login" className="px-4 py-2 rounded-lg hover:bg-white/10 transition">
              Login
            </Link>
            <Link to="/register-company" className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition">
              Start Free Trial
            </Link>
          </div>
        </nav>
      </header>

      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={18} className="mr-2" />
          Back to Home
        </Link>
      </div>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-12 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <HelpCircle className="text-blue-600" size={32} />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">
          Frequently Asked Questions
        </h1>
        <p className="text-xl text-slate-600">
          Everything you need to know about MoveMate before getting started.
        </p>
      </section>

      {/* FAQ List */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition"
            >
              <button
                className="w-full px-6 py-5 flex justify-between items-center text-left"
                onClick={() => toggleFAQ(index)}
                data-testid={`faq-question-${index}`}
              >
                <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="text-blue-600 flex-shrink-0" size={20} />
                ) : (
                  <ChevronDown className="text-slate-400 flex-shrink-0" size={20} />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5 pt-0">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Still have questions?</h2>
          <p className="text-lg text-slate-300 mb-8">
            Our team is here to help. Get in touch and we'll get back to you as soon as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition">
              Contact Us
            </Link>
            <Link to="/register-company" className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p>&copy; 2025 MoveMate. All rights reserved.</p>
          <p className="mt-2">Powered by Aztrix Media Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
